// Psychological Safety Pulse - Main Application with Firebase Integration & Password Protection
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Shield, Users, ChevronRight, ChevronLeft, Check, AlertCircle,
  TrendingUp, TrendingDown, Target, Lightbulb, BookOpen, Rocket, Zap,
  EyeOff, Download, RefreshCw, Clock, CheckCircle, XCircle, ArrowRight,
  User, ChevronDown, ChevronUp, HelpCircle, Heart, UserCheck, Award,
  Compass, MessageSquare, FileText, Wifi, WifiOff, Lock, Eye, Mail, Linkedin
} from 'lucide-react';

import QRCodeDisplay from './components/QRCodeDisplay';
import { 
  TeamScoreVisualization, 
  IndividualVsTeamVisualization, 
  ScoreGauge,
  DistributionChart,
  ProgressBar 
} from './components/Visualizations';

import { QUESTIONS, STAGES, RECOMMENDATIONS, INDIVIDUAL_ACTIONS, REFLECTION_PROMPTS } from './config/constants';
import { getScoreInterpretation } from './config/constants';
import { 
  calculateStageScores, 
  calculateTeamAverages, 
  calculateStageRange,
  calculateQuestionStats,
  getNormalizedScore,
  identifyDevelopmentAreas,
  identifyStrengthAreas,
  getPriorityStage
} from './utils/scoring';
import { generateTeamPDF, generateIndividualPDF, downloadPDF } from './utils/pdfExport';

// Firebase imports
import {
  createSession as fbCreateSession,
  validateSession,
  subscribeToSession,
  subscribeToParticipants,
  addParticipant as fbAddParticipant,
  updateParticipantResponses,
  verifyPassword
} from './config/firebase';

// Generate session code (used as fallback)
const generateSessionCode = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

// Demo data generator (for offline/demo mode)
const generateDemoParticipants = (count = 8) => {
  const names = ['Alex', 'Jordan', 'Sam', 'Morgan', 'Taylor', 'Casey', 'Riley', 'Quinn'];
  return names.slice(0, count).map((name, i) => {
    const responses = {};
    const pattern = i % 4;
    QUESTIONS.forEach(q => {
      let score;
      if (pattern === 0) score = Math.floor(Math.random() * 2) + 4;
      else if (pattern === 1) score = q.stage === 'challenger' ? Math.floor(Math.random() * 2) + 1 : Math.floor(Math.random() * 2) + 3;
      else if (pattern === 2) score = Math.floor(Math.random() * 5) + 1;
      else score = q.stage === 'inclusion' ? Math.floor(Math.random() * 2) + 2 : Math.floor(Math.random() * 2) + 4;
      responses[q.id] = Math.min(5, Math.max(1, score));
    });
    return {
      id: `p${i + 1}`,
      name,
      responses,
      status: 'completed',
      completedAt: new Date(Date.now() - Math.random() * 300000).toISOString()
    };
  });
};

// Icon mapping for stages
const STAGE_ICONS = {
  inclusion: Shield,
  learner: BookOpen,
  contributor: Rocket,
  challenger: Zap
};

const REFLECTION_ICONS = {
  self: Heart,
  teammates: Users,
  leader: UserCheck,
  coach: Compass
};

// Check if Firebase is configured
const isFirebaseConfigured = () => {
  const apiKey = import.meta.env.VITE_FIREBASE_API_KEY;
  return apiKey && apiKey !== 'YOUR_API_KEY' && apiKey.length > 10;
};

// Main App Component
export default function App() {
  // App state
  const [view, setView] = useState('home');
  const [sessionCode, setSessionCode] = useState('');
  const [session, setSession] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [participantId, setParticipantId] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState({});
  const [userName, setUserName] = useState('');
  const [nameSubmitted, setNameSubmitted] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [expandedStages, setExpandedStages] = useState({});
  const [expandedReflection, setExpandedReflection] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isOnline, setIsOnline] = useState(isFirebaseConfigured());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Password protection state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [userRole, setUserRole] = useState(null); // 'admin' or 'client'

  // Toggle stage expansion
  const toggleStage = (stage) => {
    setExpandedStages(prev => ({ ...prev, [stage]: !prev[stage] }));
  };

  // Handle "Start a Session" click - show password modal
  const handleStartSessionClick = () => {
    setShowPasswordModal(true);
    setPassword('');
    setPasswordError('');
  };

  // Verify password and create session
  const handlePasswordSubmit = async () => {
    if (!password.trim()) {
      setPasswordError('Please enter a password');
      return;
    }

    setIsLoading(true);
    setPasswordError('');

    try {
      const result = await verifyPassword(password);
      
      if (result.valid) {
        setUserRole(result.role);
        setShowPasswordModal(false);
        setPassword('');
        await createSession(result.role);
      } else {
        setPasswordError('Invalid password. Please try again.');
      }
    } catch (err) {
      console.error('Password verification error:', err);
      setPasswordError('Error verifying password. Please try again.');
    }

    setIsLoading(false);
  };

  // Create new session (Firebase or local)
  const createSession = async (role) => {
    setIsLoading(true);
    setError(null);
    
    if (isFirebaseConfigured()) {
      try {
        const code = await fbCreateSession(role === 'admin' ? 'Admin' : 'Facilitator');
        setSessionCode(code);
        setIsOnline(true);
      } catch (err) {
        console.error('Firebase error, falling back to local:', err);
        setSessionCode(generateSessionCode());
        setIsOnline(false);
      }
    } else {
      setSessionCode(generateSessionCode());
      setIsOnline(false);
    }
    
    setParticipants([]);
    setShowResults(false);
    setIsLoading(false);
    setView('facilitator');
  };

  // Subscribe to Firebase updates when session is created
  useEffect(() => {
    if (!sessionCode || !isOnline) return;

    console.log('Subscribing to session:', sessionCode);
    
    const unsubscribeSession = subscribeToSession(sessionCode, (sessionData) => {
      console.log('Session update:', sessionData);
      setSession(sessionData);
    });

    const unsubscribeParticipants = subscribeToParticipants(sessionCode, (participantsList) => {
      console.log('Participants update:', participantsList);
      setParticipants(participantsList);
    });

    return () => {
      unsubscribeSession();
      unsubscribeParticipants();
    };
  }, [sessionCode, isOnline]);

  // Join session
  const joinSession = async () => {
    if (joinCode.length !== 6) return;
    
    setIsLoading(true);
    setError(null);
    const code = joinCode.toUpperCase();
    
    if (isFirebaseConfigured()) {
      try {
        const result = await validateSession(code);
        if (result.valid) {
          setSessionCode(code);
          setIsOnline(true);
          setView('participant');
        } else {
          setError(result.error || 'Session not found');
        }
      } catch (err) {
        console.error('Validation error:', err);
        // Fall back to local mode
        setSessionCode(code);
        setIsOnline(false);
        setView('participant');
      }
    } else {
      setSessionCode(code);
      setIsOnline(false);
      setView('participant');
    }
    
    setIsLoading(false);
  };

  // Handle participant name submission and joining
  const handleJoinAsParticipant = async () => {
    if (!userName && !isAnonymous) return;
    
    const name = isAnonymous ? `Anonymous ${Date.now().toString().slice(-4)}` : userName;
    
    if (isOnline && isFirebaseConfigured()) {
      try {
        const id = await fbAddParticipant(sessionCode, { 
          name, 
          isAnonymous 
        });
        setParticipantId(id);
        console.log('Joined as participant:', id);
      } catch (err) {
        console.error('Error joining:', err);
      }
    }
    
    setNameSubmitted(true);
    setCurrentQuestion(0);
  };

  // Load demo data
  const loadDemoData = () => {
    const demoParticipants = generateDemoParticipants(8);
    setParticipants(demoParticipants);
    setIsOnline(false);
  };

  // Submit participant response
  const submitResponse = async () => {
    const name = isAnonymous ? `Anonymous ${Date.now().toString().slice(-4)}` : userName;
    
    if (isOnline && participantId && isFirebaseConfigured()) {
      try {
        await updateParticipantResponses(sessionCode, participantId, responses);
        console.log('Responses submitted to Firebase');
      } catch (err) {
        console.error('Error submitting responses:', err);
      }
    }
    
    // Create local user object for viewing results
    const newParticipant = {
      id: participantId || `p${participants.length + 1}`,
      name,
      responses: { ...responses },
      status: 'completed',
      completedAt: new Date().toISOString()
    };
    
    // If offline, add to local participants
    if (!isOnline) {
      setParticipants(prev => [...prev, newParticipant]);
    }
    
    setCurrentUser(newParticipant);
    setView('submitted');
  };

  // Export team results PDF
  const exportTeamPDF = async () => {
    setIsExporting(true);
    try {
      const sessionData = { code: sessionCode, createdAt: session?.createdAt || new Date().toISOString() };
      const doc = generateTeamPDF(sessionData, participants);
      downloadPDF(doc, `PsychSafetyPulse-${sessionCode}-Results`);
    } catch (err) {
      console.error('PDF export failed:', err);
    }
    setIsExporting(false);
  };

  // Export individual results PDF
  const exportIndividualPDF = async (participant) => {
    setIsExporting(true);
    try {
      const sessionData = { code: sessionCode, createdAt: session?.createdAt || new Date().toISOString() };
      const doc = generateIndividualPDF(participant, sessionData, participants);
      downloadPDF(doc, `PsychSafetyPulse-${participant.name}-Insights`);
    } catch (err) {
      console.error('PDF export failed:', err);
    }
    setIsExporting(false);
  };

  // Get team averages
  const teamAverages = calculateTeamAverages(participants);
  const overallScore = Object.values(teamAverages).length > 0 
    ? Object.values(teamAverages).reduce((a, b) => a + b, 0) / 4 
    : 0;

  // ============================================
  // FOOTER
  // ============================================
  const Footer = () => (
    <footer className="mt-12 py-6 border-t border-pp-rule text-center">
      <p className="text-pp-text-light text-xs">
        Alex Popa-Antohi · Team & Executive Coach · Business Psychologist ·{' '}
        <a href="mailto:contact@alexandrupopa.com" className="text-pp-teal hover:text-pp-deep transition-colors">
          contact@alexandrupopa.com
        </a>
      </p>
    </footer>
  );

  // ============================================
  // PASSWORD MODAL
  // ============================================
  const PasswordModal = () => {
    if (!showPasswordModal) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md border border-pp-rule animate-in">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-pp-warm/10 rounded-xl flex items-center justify-center">
              <Lock className="w-6 h-6 text-pp-warm" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-pp-ink font-serif font-normal">Facilitator Access</h2>
              <p className="text-pp-text text-sm">Enter your password to start a session</p>
            </div>
          </div>

          <div className="relative mb-4">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handlePasswordSubmit()}
              placeholder="Enter password"
              className="input-field pr-12"
              autoFocus
            />
            <button
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-pp-text-light hover:text-pp-ink"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>

          {passwordError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-red-600 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {passwordError}
              </p>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={() => {
                setShowPasswordModal(false);
                setPassword('');
                setPasswordError('');
              }}
              className="btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              onClick={handlePasswordSubmit}
              disabled={isLoading || !password.trim()}
              className="btn-primary flex-1"
            >
              {isLoading ? 'Verifying...' : 'Continue'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ============================================
  // HOME VIEW
  // ============================================
  const HomeView = () => (
    <div className="min-h-screen bg-pp-surface flex flex-col">
      {/* Connection status banner */}
      <div className={`py-1.5 px-4 text-center text-xs flex items-center justify-center gap-1.5 ${
        isFirebaseConfigured() ? 'bg-pp-deep/5 text-pp-teal' : 'bg-amber-50 text-amber-600'
      }`}>
        {isFirebaseConfigured() ? (
          <>
            <Wifi className="w-3 h-3" />
            <span>Real-time sync enabled</span>
          </>
        ) : (
          <>
            <WifiOff className="w-3 h-3" />
            <span>Demo mode (no Firebase configured)</span>
          </>
        )}
      </div>

      <div className="flex-1 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full">
        <div className="text-center mb-12 animate-in">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-pp-deep rounded-2xl mb-6 shadow-lg shadow-pp-warm/20">
            <Shield className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-5xl font-normal text-pp-ink mb-4 tracking-tight font-serif">
            Psychological Safety
            <span className="text-pp-warm"> Pulse</span>
          </h1>
          <p className="text-xl text-pp-text max-w-2xl mx-auto">
            A quick, evidence-based pulse check on how safe your team feels to speak up, take risks and be themselves.
          </p>
          <p className="text-sm text-pp-text-light mt-3 max-w-2xl mx-auto">
            Based on Timothy R. Clark's <em>The 4 Stages of Psychological Safety</em> framework and research by Amy Edmondson.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <button
            onClick={handleStartSessionClick}
            disabled={isLoading}
            className="group bg-pp-warm p-8 rounded-2xl shadow-xl hover:shadow-pp-warm/20 transition-all duration-300 hover:-translate-y-1 text-left disabled:opacity-50"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <FileText className="w-7 h-7 text-white" />
              </div>
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-white/60" />
                <ChevronRight className="w-6 h-6 text-white/60 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2 font-serif font-normal">Start a Session</h2>
            <p className="text-white/90">Create a session and share a QR code for your team to join</p>
          </button>

          <div className="bg-white p-8 rounded-2xl border border-pp-rule shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="w-14 h-14 bg-pp-wash rounded-xl flex items-center justify-center">
                <Users className="w-7 h-7 text-pp-ink-soft" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-pp-ink mb-2 text-left font-serif font-normal">Join a Session</h2>
            <p className="text-pp-text text-left mb-4">Enter the 6-character code shown on the facilitator's screen</p>
            <div className="flex gap-3">
              <input
                type="text"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase().slice(0, 6))}
                placeholder="ABCD12"
                className="input-field text-center text-xl tracking-widest font-mono"
                maxLength={6}
              />
              <button
                onClick={joinSession}
                disabled={joinCode.length !== 6 || isLoading}
                className="btn-primary"
              >
                {isLoading ? '...' : 'Join'}
              </button>
            </div>
            {error && (
              <p className="text-red-600 text-sm mt-2">{error}</p>
            )}
          </div>
        </div>

        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(STAGES).map(([key, stage]) => {
            const Icon = STAGE_ICONS[key];
            return (
              <div key={key} className="bg-white rounded-xl p-4 border border-pp-rule shadow-sm">
                <Icon className="w-6 h-6 mb-2" style={{ color: stage.color }} />
                <h3 className="text-sm font-semibold text-pp-ink">{stage.name}</h3>
                <p className="text-xs text-pp-text-light mt-1">{stage.question}</p>
              </div>
            );
          })}
        </div>

        {/* About the Coach */}
        <div className="mt-6 bg-white rounded-2xl border border-pp-rule p-6 shadow-sm">
          <h2 className="text-xl font-normal text-pp-ink font-serif mb-4">About the Coach</h2>
          <p className="text-pp-text text-sm leading-relaxed mb-4">
            Alex Popa-Antohi is a team and executive coach and business psychologist based in Vienna. He built this tool drawing on his background in psychometrics (BPS-qualified) and over 15 years of coaching leadership teams across 20+ countries. Before coaching, he led team effectiveness programmes globally at Vodafone.
          </p>
          <p className="text-pp-text text-sm leading-relaxed mb-5">
            Want to talk about your team? Reach out to Alex anytime.
          </p>
          <div className="flex items-center gap-3 text-sm">
            <a
              href="mailto:contact@alexandrupopa.com"
              className="text-pp-teal hover:text-pp-deep transition-colors"
            >
              contact@alexandrupopa.com
            </a>
            <span className="text-pp-rule">|</span>
            <a
              href="https://www.linkedin.com/in/presspause/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-pp-teal hover:text-pp-deep transition-colors"
            >
              Connect on LinkedIn
            </a>
          </div>
        </div>
        <Footer />
      </div>
      </div>

      <PasswordModal />
    </div>
  );

  // ============================================
  // FACILITATOR VIEW
  // ============================================
  const FacilitatorView = () => {
    const completedCount = participants.filter(p => p.status === 'completed').length;
    
    return (
      <div className="min-h-screen bg-pp-surface p-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => {
                setView('home');
                setUserRole(null);
              }}
              className="flex items-center gap-2 text-pp-text hover:text-pp-ink transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
              Back
            </button>
            <div className="flex items-center gap-4">
              {/* Connection status */}
              <div className="flex items-center gap-2 text-sm">
                {isOnline ? (
                  <>
                    <Wifi className="w-4 h-4 text-pp-teal" />
                    <span className="text-pp-teal">Live</span>
                  </>
                ) : (
                  <>
                    <WifiOff className="w-4 h-4 text-amber-600" />
                    <span className="text-amber-600">Demo</span>
                  </>
                )}
              </div>
              {userRole && (
                <span className="bg-pp-wash text-pp-ink-soft px-3 py-1 rounded-full text-sm">
                  {userRole === 'admin' ? 'Admin' : 'Client'}
                </span>
              )}
              <button
                onClick={loadDemoData}
                className="btn-secondary flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Demo Data
              </button>
              {completedCount >= 3 && (
                <button
                  onClick={() => { setShowResults(true); setView('results'); }}
                  className="btn-primary flex items-center gap-2"
                >
                  View Results
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* QR Code Section */}
            <div className="card p-8 text-center">
              <h2 className="text-2xl font-normal text-pp-ink mb-2 font-serif">Scan to Join</h2>
              <p className="text-pp-text mb-6">Your team can scan this code or enter the session code below</p>
              <QRCodeDisplay sessionCode={sessionCode} size={200} />
            </div>

            {/* Participants Section */}
            <div className="card p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-normal text-pp-ink font-serif">Participants</h2>
                <span className="bg-pp-teal/10 text-pp-teal px-3 py-1 rounded-full text-sm font-medium">
                  {completedCount} completed
                </span>
              </div>

              {participants.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-pp-wash rounded-full flex items-center justify-center mx-auto mb-4">
                    <Clock className="w-8 h-8 text-pp-text-light" />
                  </div>
                  <p className="text-pp-text">Waiting for participants to join...</p>
                  <p className="text-pp-text-light text-sm mt-2">
                    {isOnline 
                      ? "Share the code or QR with your team"
                      : "Click 'Demo Data' to simulate participants"
                    }
                  </p>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {participants.map((p) => (
                    <div key={p.id} className="flex items-center justify-between bg-pp-wash rounded-xl p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-pp-deep rounded-full flex items-center justify-center text-white font-bold">
                          {p.name.charAt(0)}
                        </div>
                        <div>
                          <p className="text-pp-ink font-medium">{p.name}</p>
                          <p className="text-pp-text-light text-sm">
                            {p.status === 'completed' 
                              ? `Completed ${p.completedAt ? new Date(p.completedAt).toLocaleTimeString() : ''}`
                              : 'In progress...'}
                          </p>
                        </div>
                      </div>
                      {p.status === 'completed' ? (
                        <CheckCircle className="w-5 h-5 text-pp-teal" />
                      ) : (
                        <Clock className="w-5 h-5 text-pp-warm" />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {participants.length > 0 && completedCount < 3 && (
                <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <p className="text-amber-600 text-sm flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    At least 3 completed responses are needed before results can be viewed
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Progress indicator */}
          <div className="mt-8 card p-6">
            <h3 className="text-lg font-semibold text-pp-ink mb-4">Session Progress</h3>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <ProgressBar current={completedCount} total={Math.max(8, participants.length)} />
              </div>
              <span className="text-pp-text text-sm">{completedCount} completed</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ============================================
  // PARTICIPANT VIEW
  // ============================================
  const ParticipantView = () => {
    const question = QUESTIONS[currentQuestion];
    const progress = ((currentQuestion + 1) / QUESTIONS.length) * 100;
    const stageInfo = question ? STAGES[question.stage] : null;

    // Name entry screen
    if (!nameSubmitted && !isAnonymous) {
      return (
        <div className="min-h-screen bg-pp-surface flex items-center justify-center p-4">
          <div className="w-full max-w-md animate-in">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-pp-deep rounded-2xl mb-4">
                <User className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-normal text-pp-ink mb-2 font-serif">Welcome</h1>
              <p className="text-pp-text">
                Session: <span className="text-pp-teal font-mono">{sessionCode}</span>
              </p>
            </div>

            <div className="card p-6">
              <label className="block text-sm font-medium text-pp-ink-soft mb-2">Your Name</label>
              <input
                type="text"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                placeholder="Enter your first name"
                className="input-field mb-4"
                autoFocus
              />
              
              <button
                onClick={handleJoinAsParticipant}
                disabled={!userName}
                className="btn-primary w-full"
              >
                Begin the Pulse Check
              </button>

              <div className="flex items-center gap-3 mt-4 pt-4 border-t border-pp-rule">
                <button
                  onClick={() => {
                    setIsAnonymous(true);
                    handleJoinAsParticipant();
                  }}
                  className="flex-1 py-2 text-pp-text text-sm hover:text-pp-ink transition-colors flex items-center justify-center gap-2"
                >
                  <EyeOff className="w-4 h-4" />
                  Continue anonymously
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-pp-surface flex flex-col">
        {/* Header */}
        <div className="p-4 bg-white border-b border-pp-rule">
          <div className="flex items-center justify-between mb-3">
            <span className="text-pp-text text-sm">
              Question {currentQuestion + 1} of {QUESTIONS.length}
            </span>
            <span 
              className="stage-badge"
              style={{ backgroundColor: `${stageInfo?.color}20`, color: stageInfo?.color }}
            >
              {stageInfo?.name}
            </span>
          </div>
          <ProgressBar current={currentQuestion + 1} total={QUESTIONS.length} />
        </div>

        {/* Question */}
        <div className="flex-1 flex flex-col justify-center p-6">
          <div className="max-w-md mx-auto w-full animate-in">
            <p className="text-xl md:text-2xl text-pp-ink font-medium leading-relaxed mb-8 text-center">
              "{question?.text}"
            </p>

            {/* Rating Scale */}
            <div className="space-y-3">
              {[
                { value: 1, label: 'Strongly Disagree' },
                { value: 2, label: 'Disagree' },
                { value: 3, label: 'Neutral' },
                { value: 4, label: 'Agree' },
                { value: 5, label: 'Strongly Agree' }
              ].map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => setResponses({ ...responses, [question.id]: value })}
                  className={`w-full py-4 px-6 rounded-xl border-2 transition-all duration-200 flex items-center justify-between ${
                    responses[question.id] === value
                      ? 'border-pp-warm bg-pp-warm/10 text-pp-ink'
                      : 'border-pp-rule bg-white text-pp-ink-soft hover:border-pp-teal-light'
                  }`}
                >
                  <span>{label}</span>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    responses[question.id] === value
                      ? 'border-pp-warm bg-pp-warm'
                      : 'border-pp-rule'
                  }`}>
                    {responses[question.id] === value && <Check className="w-4 h-4 text-white" />}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="p-4 bg-white border-t border-pp-rule">
          <div className="flex gap-3 max-w-md mx-auto">
            {currentQuestion > 0 && (
              <button
                onClick={() => setCurrentQuestion(currentQuestion - 1)}
                className="btn-secondary flex-1"
              >
                Back
              </button>
            )}
            <button
              onClick={() => {
                if (currentQuestion < QUESTIONS.length - 1) {
                  setCurrentQuestion(currentQuestion + 1);
                } else {
                  submitResponse();
                }
              }}
              disabled={!responses[question?.id]}
              className="btn-primary flex-1"
            >
              {currentQuestion < QUESTIONS.length - 1 ? 'Next' : 'Submit'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ============================================
  // SUBMITTED VIEW
  // ============================================
  const SubmittedView = () => (
    <div className="min-h-screen bg-pp-surface flex items-center justify-center p-4">
      <div className="text-center max-w-md animate-in">
        <div className="w-20 h-20 bg-pp-deep rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-pp-warm/20">
          <Check className="w-10 h-10 text-white" />
        </div>
        <h1 className="text-3xl font-normal text-pp-ink mb-4 font-serif">Thank You</h1>
        <p className="text-pp-text mb-8">
          Your responses have been recorded. The facilitator will share the team's results with you shortly.
        </p>
        <button
          onClick={() => { setSelectedParticipant(currentUser); setView('individual'); }}
          className="btn-primary"
        >
          View Your Personal Insights
        </button>
      </div>
    </div>
  );

  // ============================================
  // TEAM RESULTS VIEW
  // ============================================
  const ResultsView = () => {
    const interpretation = getScoreInterpretation(overallScore);
    const priorityStage = getPriorityStage(teamAverages);
    const completedParticipants = participants.filter(p => p.status === 'completed');

    return (
      <div className="min-h-screen bg-pp-surface p-4 md:p-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => setView('facilitator')}
              className="flex items-center gap-2 text-pp-text hover:text-pp-ink transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
              Back to Session
            </button>
            <button 
              onClick={exportTeamPDF}
              disabled={isExporting}
              className="btn-secondary flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              {isExporting ? 'Exporting...' : 'Export PDF'}
            </button>
          </div>

          {/* Overall Score */}
          <div className="card p-8 mb-6 text-center">
            <h1 className="text-2xl font-normal text-pp-ink mb-6 font-serif">Your Team's Psychological Safety Pulse</h1>
            <ScoreGauge score={overallScore} interpretation={interpretation} />
            <p className="text-pp-text mt-4">
              {interpretation.description} • {completedParticipants.length} participants
            </p>
          </div>

          {/* Reading Guide */}
          <div className="bg-pp-wash rounded-xl p-4 mb-6 border border-pp-rule">
            <p className="text-sm text-pp-text mb-3 font-medium">How to read the charts:</p>
            <div className="flex flex-wrap items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-8 h-3 rounded-sm bg-pp-teal opacity-30" />
                <span className="text-pp-ink-soft">Shaded band = range of responses</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-0.5 h-5 rounded-full bg-pp-teal opacity-60" />
                <span className="text-pp-ink-soft">Vertical lines = min & max</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full border-4 border-pp-teal bg-white" />
                <span className="text-pp-ink-soft">Circle = team average</span>
              </div>
            </div>
          </div>

          {/* Stage Breakdown */}
          <div className="space-y-4 mb-8">
            {Object.entries(STAGES).map(([key, stage]) => {
              const score = teamAverages[key] || 0;
              const range = calculateStageRange(participants, key);
              const Icon = STAGE_ICONS[key];
              const isPriority = key === priorityStage;
              const isExpanded = expandedStages[key];
              const stageQuestions = QUESTIONS.filter(q => q.stage === key);

              return (
                <div 
                  key={key} 
                  className={`card overflow-hidden ${isPriority ? 'border-pp-warm/50' : ''}`}
                >
                  <div className="p-5">
                    {isPriority && (
                      <span className="text-xs text-pp-warm font-medium mb-2 block">
                        Priority Focus Area
                      </span>
                    )}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-4">
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: `${stage.color}20` }}
                        >
                          <Icon className="w-6 h-6" style={{ color: stage.color }} />
                        </div>
                        <div>
                          <h3 className="text-pp-ink font-semibold text-lg">{stage.name}</h3>
                          {range.spread > 1.5 && (
                            <span className="text-xs bg-pp-warm-light text-pp-warm px-2 py-0.5 rounded-full">
                              Wide spread of responses
                            </span>
                          )}
                        </div>
                      </div>
                      <button 
                        onClick={() => toggleStage(key)}
                        className="flex items-center gap-2 text-pp-text hover:text-pp-ink transition-colors px-3 py-2 rounded-lg hover:bg-pp-wash"
                      >
                        <span className="text-sm">{isExpanded ? 'Hide' : 'Show'} statements</span>
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </div>
                    
                    <TeamScoreVisualization 
                      average={score} 
                      min={range.min} 
                      max={range.max} 
                      color={stage.color}
                    />
                  </div>

                  {isExpanded && (
                    <div className="border-t border-pp-rule p-5 bg-pp-wash">
                      <h4 className="text-sm font-medium text-pp-text mb-3">Statement-level breakdown</h4>
                      {stageQuestions.map(q => {
                        const stats = calculateQuestionStats(participants, q.id);
                        return (
                          <div key={q.id} className="bg-white rounded-lg p-4 mb-2 border border-pp-rule">
                            <p className="text-pp-ink-soft text-sm mb-3">"{q.text}"</p>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-pp-ink font-semibold">Avg: {stats.average.toFixed(1)}</span>
                              <span className="text-pp-text-light text-xs">
                                Range: {stats.min.toFixed(1)} – {stats.max.toFixed(1)}
                              </span>
                            </div>
                            <DistributionChart distribution={stats.distribution} color={stage.color} />
                            {!q.positive && (
                              <p className="text-xs text-pp-warm mt-2 flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" />
                                Reverse-scored — a lower response here indicates higher safety
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Recommendations */}
          {priorityStage && (
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-[#EFF6F5] rounded-2xl p-6 border border-pp-teal-light">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-pp-deep rounded-xl flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-normal text-pp-ink font-serif">What to do</h2>
                    <p className="text-pp-teal text-sm">Team focus: {STAGES[priorityStage]?.name}</p>
                  </div>
                </div>
                <ul className="space-y-3">
                  {RECOMMENDATIONS[priorityStage]?.dos.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-pp-ink-soft">
                      <Check className="w-5 h-5 text-pp-teal shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-red-50 rounded-2xl p-6 border border-red-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-red-500 rounded-xl flex items-center justify-center">
                    <XCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-normal text-pp-ink font-serif">What to avoid</h2>
                    <p className="text-red-600 text-sm">Behaviours that undermine safety</p>
                  </div>
                </div>
                <ul className="space-y-3">
                  {RECOMMENDATIONS[priorityStage]?.donts.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-pp-ink-soft">
                      <XCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Individual Results */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-normal text-pp-ink font-serif">Individual Insights</h2>
              <p className="text-pp-text text-sm">Select a name to see personalised insights</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {completedParticipants.map((p) => {
                const pScores = calculateStageScores(p.responses);
                const pOverall = Object.values(pScores).reduce((a, b) => a + b, 0) / 4;
                return (
                  <button
                    key={p.id}
                    onClick={() => { setSelectedParticipant(p); setView('individual'); }}
                    className="bg-pp-wash rounded-xl p-4 hover:bg-pp-teal-light/30 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-pp-deep rounded-full flex items-center justify-center text-white font-bold">
                        {p.name.charAt(0)}
                      </div>
                      <span className="text-pp-ink font-medium">{p.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold text-pp-ink">{pOverall.toFixed(1)}</span>
                      {pOverall < overallScore - 0.5 && <TrendingDown className="w-4 h-4 text-pp-warm" />}
                      {pOverall > overallScore + 0.5 && <TrendingUp className="w-4 h-4 text-pp-teal" />}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          <Footer />
        </div>
      </div>
    );
  };

  // ============================================
  // INDIVIDUAL VIEW
  // ============================================
  const IndividualView = () => {
    if (!selectedParticipant) return null;
    
    const pScores = calculateStageScores(selectedParticipant.responses);
    const pOverall = Object.values(pScores).reduce((a, b) => a + b, 0) / 4;
    const interpretation = getScoreInterpretation(pOverall);
    
    const developmentAreas = identifyDevelopmentAreas(selectedParticipant.responses, teamAverages);
    const strengthAreas = identifyStrengthAreas(selectedParticipant.responses, teamAverages);

    const displayStrengths = strengthAreas.length > 0 ? strengthAreas : 
      Object.entries(pScores).sort(([,a], [,b]) => b - a).slice(0, 1).map(([s]) => s);
    const displayDevelopment = developmentAreas.length > 0 ? developmentAreas : 
      Object.entries(pScores).sort(([,a], [,b]) => a - b).slice(0, 1).map(([s]) => s);

    return (
      <div className="min-h-screen bg-pp-surface p-4 md:p-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => setView(showResults ? 'results' : 'submitted')}
              className="flex items-center gap-2 text-pp-text hover:text-pp-ink transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
              Back
            </button>
            <button 
              onClick={() => exportIndividualPDF(selectedParticipant)}
              disabled={isExporting}
              className="btn-secondary flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              {isExporting ? 'Exporting...' : 'Export PDF'}
            </button>
          </div>

          {/* Personal Header */}
          <div className="card p-6 mb-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 bg-pp-deep rounded-full flex items-center justify-center text-white text-2xl font-bold">
                {selectedParticipant.name.charAt(0)}
              </div>
              <div>
                <h1 className="text-2xl font-normal text-pp-ink font-serif">{selectedParticipant.name}'s Insights</h1>
                <p className="text-pp-text">Your personal psychological safety pulse</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-6">
              <div className="bg-pp-wash rounded-xl p-4">
                <p className="text-pp-text text-sm mb-1">Your Reading</p>
                <p className="text-3xl font-bold" style={{ color: interpretation.color }}>{pOverall.toFixed(1)}</p>
              </div>
              <div className="bg-pp-wash rounded-xl p-4">
                <p className="text-pp-text text-sm mb-1">Team Average</p>
                <p className="text-3xl font-bold text-pp-ink-soft">{overallScore.toFixed(1)}</p>
              </div>
            </div>
          </div>

          {/* Reading Guide */}
          <div className="bg-pp-wash rounded-xl p-4 mb-6 border border-pp-rule">
            <p className="text-sm text-pp-text mb-3 font-medium">How to read your charts:</p>
            <div className="flex flex-wrap items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-pp-teal" />
                <span className="text-pp-ink-soft">Solid circle = your reading</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-3 rounded-sm bg-pp-text-light opacity-40" />
                <span className="text-pp-ink-soft">Grey band = team range</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-0.5 h-5 bg-pp-ink rounded-full" />
                <span className="text-pp-ink-soft">Dark line = team average</span>
              </div>
            </div>
          </div>

          {/* Stage Comparison */}
          <div className="card p-6 mb-6">
            <h2 className="text-xl font-normal text-pp-ink mb-4 font-serif">Your Readings vs Team</h2>
            <div className="space-y-6">
              {Object.entries(STAGES).map(([key, stage]) => {
                const Icon = STAGE_ICONS[key];
                const myScore = pScores[key];
                const teamScore = teamAverages[key] || 0;
                const range = calculateStageRange(participants, key);
                const diff = myScore - teamScore;
                
                return (
                  <div key={key} className="bg-pp-wash rounded-xl p-4">
                    <div className="flex items-center gap-4 mb-1">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                        style={{ backgroundColor: `${stage.color}20` }}
                      >
                        <Icon className="w-5 h-5" style={{ color: stage.color }} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="text-pp-ink font-medium">{stage.name}</span>
                          <span className={`text-sm font-medium ${
                            diff > 0.3 ? 'text-pp-teal' : diff < -0.3 ? 'text-pp-warm' : 'text-pp-text'
                          }`}>
                            {diff > 0 ? '+' : ''}{diff.toFixed(1)} vs team
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <IndividualVsTeamVisualization 
                      myScore={myScore}
                      teamAverage={teamScore}
                      teamMin={range.min}
                      teamMax={range.max}
                      color={stage.color}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Strengths */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-pp-deep rounded-xl flex items-center justify-center">
                <Award className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-normal text-pp-ink font-serif">Your Strengths</h2>
                <p className="text-pp-teal text-sm">Areas where your pulse is strongest</p>
              </div>
            </div>

            <div className="space-y-4">
              {displayStrengths.map((stage) => {
                const Icon = STAGE_ICONS[stage];
                return (
                  <div key={stage} className="bg-[#EFF6F5] rounded-2xl border border-pp-teal-light overflow-hidden">
                    <div className="flex items-center gap-2 px-5 py-3 bg-pp-deep/5 border-b border-pp-teal-light">
                      <Icon className="w-5 h-5 text-pp-teal" />
                      <h3 className="text-pp-teal font-semibold">{STAGES[stage].name}</h3>
                    </div>
                    <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-pp-teal-light">
                      <div className="p-4">
                        <h4 className="text-xs font-semibold text-pp-teal uppercase tracking-wide mb-3 flex items-center gap-1.5">
                          <Lightbulb className="w-3.5 h-3.5" />
                          Why this is your strength
                        </h4>
                        <ul className="space-y-2">
                          {INDIVIDUAL_ACTIONS[stage]?.leveraging.slice(0, 3).map((item, i) => (
                            <li key={i} className="flex items-start gap-2 text-pp-ink-soft text-sm">
                              <span className="text-pp-teal mt-1">•</span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="p-4">
                        <h4 className="text-xs font-semibold text-pp-teal uppercase tracking-wide mb-3 flex items-center gap-1.5">
                          <Check className="w-3.5 h-3.5" />
                          Actions to help your team
                        </h4>
                        <ul className="space-y-2">
                          {INDIVIDUAL_ACTIONS[stage]?.high.slice(0, 3).map((action, i) => (
                            <li key={i} className="flex items-start gap-2 text-pp-ink-soft text-sm">
                              <span className="text-pp-teal mt-1">•</span>
                              <span>{action}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Growth Opportunities */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-pp-warm rounded-xl flex items-center justify-center">
                <Target className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-normal text-pp-ink font-serif">Growth Opportunities</h2>
                <p className="text-pp-warm text-sm">Areas where there's room to grow</p>
              </div>
            </div>

            <div className="space-y-4">
              {displayDevelopment.slice(0, 2).map((stage) => {
                const Icon = STAGE_ICONS[stage];
                return (
                  <div key={stage} className="bg-pp-warm-light rounded-2xl border border-pp-warm/20 overflow-hidden">
                    <div className="flex items-center gap-2 px-5 py-3 bg-pp-warm/5 border-b border-pp-warm/20">
                      <Icon className="w-5 h-5 text-pp-warm" />
                      <h3 className="text-pp-warm font-semibold">{STAGES[stage].name}</h3>
                    </div>
                    <div className="p-4">
                      <h4 className="text-xs font-semibold text-pp-warm uppercase tracking-wide mb-3 flex items-center gap-1.5">
                        <ArrowRight className="w-3.5 h-3.5" />
                        Actions to try
                      </h4>
                      <ul className="space-y-2">
                        {INDIVIDUAL_ACTIONS[stage]?.low.slice(0, 3).map((action, i) => (
                          <li key={i} className="flex items-start gap-2 text-pp-ink-soft text-sm">
                            <span className="text-pp-warm mt-1">•</span>
                            <span>{action}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Reflection Prompts */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-pp-wash rounded-xl flex items-center justify-center">
                <HelpCircle className="w-5 h-5 text-pp-ink-soft" />
              </div>
              <div>
                <h2 className="text-xl font-normal text-pp-ink font-serif">Reflection Prompts</h2>
                <p className="text-pp-text text-sm">What do you need from the people around you?</p>
              </div>
            </div>

            <div className="space-y-3">
              {Object.entries(REFLECTION_PROMPTS).map(([key, source]) => {
                const Icon = REFLECTION_ICONS[key];
                const isExpanded = expandedReflection === key;

                return (
                  <div key={key} className="border border-pp-rule rounded-2xl overflow-hidden bg-white">
                    <button
                      onClick={() => setExpandedReflection(isExpanded ? null : key)}
                      className="w-full flex items-center justify-between p-4 hover:bg-pp-wash transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${source.color}20` }}
                        >
                          <Icon className="w-5 h-5" style={{ color: source.color }} />
                        </div>
                        <span className="text-pp-ink font-medium">{source.title}</span>
                      </div>
                      {isExpanded ? <ChevronUp className="w-5 h-5 text-pp-text" /> : <ChevronDown className="w-5 h-5 text-pp-text" />}
                    </button>

                    {isExpanded && (
                      <div className="px-4 pb-4 space-y-3">
                        {displayDevelopment.slice(0, 2).map((stage) => (
                          <div key={stage} className="bg-pp-wash rounded-xl p-4">
                            <h4 className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: STAGES[stage].color }}>
                              {STAGES[stage].name}
                            </h4>
                            <ul className="space-y-2.5">
                              {REFLECTION_PROMPTS[key].prompts[stage]?.slice(0, 3).map((prompt, i) => (
                                <li key={i} className="flex items-start gap-2.5 text-pp-ink-soft text-sm">
                                  <MessageSquare className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: source.color }} />
                                  <span>{prompt}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <Footer />
        </div>
      </div>
    );
  };

  // ============================================
  // VIEW ROUTER
  // ============================================
  switch (view) {
    case 'home':
      return HomeView();
    case 'facilitator':
      return FacilitatorView();
    case 'participant':
      return ParticipantView();
    case 'submitted':
      return SubmittedView();
    case 'results':
      return ResultsView();
    case 'individual':
      return IndividualView();
    default:
      return HomeView();
  }
}
